// src/features/sso-auth/hooks/useSession.ts
import { useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

interface UseSessionOptions {
  checkInterval?: number; // in milliseconds
  onSessionExpired?: () => void;
  onTokenRefreshed?: () => void;
}

export const useSession = (options: UseSessionOptions = {}) => {
  const {
    checkInterval = 5 * 60 * 1000, // 5 minutes default
    onSessionExpired,
    onTokenRefreshed,
  } = options;

  const {
    isAuthenticated,
    checkSession,
    accessToken,
    refreshToken,
    logout,
    getLocales,
  } = useAuthStore();

  const performSessionCheck = useCallback(async () => {
    if (!isAuthenticated || !accessToken || !refreshToken) {
      return false;
    }

    try {
      const isValid = await checkSession();

      if (!isValid) {
        onSessionExpired?.();
        return false;
      }

      onTokenRefreshed?.();
      return true;
    } catch (error) {
      console.error('Session check failed:', error);
      onSessionExpired?.();
      return false;
    }
  }, [isAuthenticated, accessToken, refreshToken, checkSession, onSessionExpired, onTokenRefreshed]);

  // Periodic session check
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(performSessionCheck, checkInterval);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkInterval, performSessionCheck]);

  // Check session on mount and when authentication status changes
  useEffect(() => {
    if (isAuthenticated) {
      void performSessionCheck();
      void getLocales();
    }
  }, [isAuthenticated, performSessionCheck]);

  // Check session when the page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        void performSessionCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, performSessionCheck]);

  return {
    isAuthenticated,
    checkSession: performSessionCheck,
    logout,
  };
};